import React, {useState} from 'react'
import {useIntl} from 'react-intl'
import {Box, Heading, HStack} from '@salesforce/retail-react-app/app/components/shared/ui'

import Seo from '@salesforce/retail-react-app/app/components/seo'

import {OutletSubscription} from './partials/outlet-subscription'
import {
    ACCOUNT_ORDERS_URL,
    FAQ_URL,
    LEGAL_MENTIONS_URL,
    MAX_CACHE_AGE_STATIC_PAGES,
    PRIVACY_POLICY_URL,
    STALE_WHILE_REVALIDATE,
    TRACK_MY_ORDER_URL
} from '../../constants'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import Link from '../../components/link'
import OutletCode from './partials/outlet-code'
import PageNotFound from '../page-not-found'
import useMultiSite from '../../hooks/use-multi-site'
import LoadingSpinner from '../../components/loading-spinner'
import PropTypes from 'prop-types'

/**
 * Newsletter component for displaying a newsletter subscription page.
 *
 * This component fetches and displays the newsletter page content, including
 * a subscription form and optional page designer components.
 *
 * @returns {JSX.Element} The rendered newsletter page.
 */

const OutletEntrance = ({isOutletOpened, isCampaignStatusLoading}) => {
    // useServerContext is a special hook introduced in v3 PWA Kit SDK.
    // It replaces the legacy `getProps` and provide a react hook interface for SSR.
    // it returns the request and response objects on the server side,
    // and these objects are undefined on the client side.
    const {res} = useServerContext()
    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE_STATIC_PAGES}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }

    const {isOutletSite} = useMultiSite()
    const {formatMessage} = useIntl()
    const [isGetCode, setIsGetCode] = useState(false)
    const [isPrefilled, setIsPrefilled] = useState(false)

    // Useful Links urls and labels
    const links = [
        {
            url: PRIVACY_POLICY_URL,
            label: formatMessage({
                id: 'outlet_entrance.link.privacypolicy',
                defaultMessage: 'Privacy policy'
            })
        },
        {
            url: FAQ_URL,
            label: formatMessage({
                id: 'outlet_entrance.link.faq',
                defaultMessage: 'FAQ'
            })
        },
        {
            url: LEGAL_MENTIONS_URL,
            label: formatMessage({
                id: 'outlet_entrance.link.legalmentions',
                defaultMessage: 'Legal mentions'
            })
        },
        {
            url: ACCOUNT_ORDERS_URL,
            label: formatMessage({
                id: 'outlet_entrance.link.orderhistory',
                defaultMessage: 'Order history'
            })
        },
        {
            url: TRACK_MY_ORDER_URL,
            label: formatMessage({
                id: 'outlet_entrance.link.trackmyorder',
                defaultMessage: 'Order tracking'
            })
        }
    ]

    if (!isOutletSite) {
        return <PageNotFound />
    }

    return (
        <>
            <Box
                data-testid="outlet-entrance-page"
                display={'flex'}
                backgroundImage={`url('${getAssetUrl('static/img/outlet/entrance-lg.jpg')}')`}
                backgroundPosition="center"
                backgroundRepeat="no-repeat"
                backgroundSize={'cover'}
                px={['16px', null, '171px']}
                minHeight={[678, null, 860]}
                flex={1}
            >
                <Seo
                    title={formatMessage({
                        id: 'outlet_entrance.seo.title',
                        defaultMessage: 'Outlet entrance'
                    })}
                    description={formatMessage({
                        id: 'outlet_entrance.seo.desc',
                        defaultMessage: 'Outlet entrance page'
                    })}
                />

                {!isCampaignStatusLoading ? (
                    isOutletOpened === false || isGetCode ? (
                        <OutletSubscription
                            isGetCode={isGetCode}
                            setIsGetCode={setIsGetCode}
                            setIsPrefilled={setIsPrefilled}
                        />
                    ) : (
                        <OutletCode setIsGetCode={setIsGetCode} isPrefilled={isPrefilled} />
                    )
                ) : (
                    <LoadingSpinner size="lg" />
                )}
            </Box>
            {isOutletOpened === false && !isCampaignStatusLoading && (
                <Box p={'64px 16px'} textAlign={'center'}>
                    <Heading
                        as="h2"
                        fontSize={'3xl'}
                        textAlign="center"
                        textTransform={'uppercase'}
                        mb={16}
                    >
                        {formatMessage({
                            id: 'outlet_entrance.links.title',
                            defaultMessage: 'Useful links'
                        })}
                    </Heading>
                    <HStack
                        alignItems={'center'}
                        justifyContent={'flex-start'}
                        display={'inline-flex'}
                        gap={16}
                        flexWrap={'wrap'}
                    >
                        {links.map((link) => (
                            <Link
                                key={link.url}
                                variant="secondary"
                                href={link.url}
                                size="md"
                                flex={['0 0 calc((100vw - 48px) / 2)', null, 1]}
                                whiteSpace={['normal', null, null, 'nowrap']}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </HStack>
                </Box>
            )}
        </>
    )
}

OutletEntrance.propTypes = {
    isCampaignStatusLoading: PropTypes.bool,
    isOutletOpened: PropTypes.bool
}

export default OutletEntrance
